<template>
<div class="vert-bar-chart-all" :style="{height: ((title?65:0)+10+this.fullHeight)+'px'}">
  <h2 v-if="title">{{ title }}</h2>
  <svg :height="height" :width="width" class="vert-bar-chart"></svg>
  <ul v-if="showLegend" class="vert-bar-chart-legend">
    <li :style="{color: getLegendTextColor(data)}" v-for="(data, index) in normalizedChartData" :key="index">
      {{ getLegendText(data) }}
    </li>
  </ul>
</div>
</template>

<script>
import Vue from 'vue'
import _ from 'lodash'
import * as d3 from 'd3'

export default Vue.extend({
  name: 'vert-bar-chart',
  props: {
    title: {
      type: String,
      default: ''
    },
    showTooltip: {
      type: Boolean,
      default: true
    },
    showText: {
      type: Boolean,
      default: true
    },
    getRectColor: {
      type: Function,
      default: item => '#888888'
    },
    getText: {
      type: Function,
      default: item => item.name
    },
    getTextColor: {
      type: Function,
      default: item => '#000'
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    getLegendText: {
      type: Function,
      default: item => item.name
    },
    getLegendTextColor: {
      type: Function,
      default: item => '#000'
    },
    maxWidth: {
      type: Number,
      default: 800
    },
    height: {
      type: Number,
      default: 300
    },
    incrustText: {
      type: Boolean,
      default: true
    },
    textAngle: {
      type: Number,
      default: 0
    },
    textOffset: {
      // Utilisé lorsque incrustText est vrai
      type: Number,
      default: 70
    },
    chartData: {
      type: [Array, Object],
      default: item => []
    }
  },

  data: function() {
    return {
      width: 0,
      animate: true
    }
  },

  watch: {
    normalizedChartData: {
      handler: 'renderChartDebounced',
      deep: true
    },
    height: 'renderChartDebounced',
    width: 'renderChartDebounced',
    barWidth: 'renderChartDebounced'
  },

  computed: {
    fullHeight() {
      return this.height + (this.incrustText ? 0 : this.textOffset)
    },
    barWidth() {
      return this.normalizedChartData ? this.width / this.normalizedChartData.length : this.width
    },
    normalizedChartData: function() {
      return this.chartData
    }
  },

  mounted() {
    window.addEventListener('resize', this.onResize)
    this.onResize()
    this.renderChartDebounced()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },

  methods: {
    onResize() {
      // Pour la responsivité
      let fullWidth = this.$el.offsetWidth
      let computedWidth = null
      if (!this.showLegend || fullWidth <= 600) {
        // Légende au-dessous
        computedWidth = this.$el.offsetWidth
      } else {
        // Tout sur la largeur
        computedWidth = this.$el.offsetWidth / 2
      }
      // Prise en compte de la largeur max
      this.width = computedWidth > this.maxWidth ? this.maxWidth : computedWidth
    },

    renderChartDebounced: _.debounce(function() {
      this.renderChart()
    }, 1),
    renderChart() {
      let data = this.normalizedChartData
      let height = this.height
      let barWidth = this.barWidth
      let heightPad = 0

      let x = d3.scaleBand().rangeRound([0, this.width])
        .domain(data.map(this.getText))

      let y = d3.scaleLinear()
        .domain([0, d3.max(data, d => d.val)])
        .range([0, height - heightPad])

      let chart = d3.select(this.$el).select('svg')
        .attr('width', this.width)
        .attr('height', this.fullHeight)

      let d = chart.selectAll('g')
        .data(data)

      d.exit().remove()
      let g = d.enter().append('g')
        .merge(d)
        .attr('transform', (d, i) => 'translate(' + i * barWidth + ',0)')
      g.selectAll('rect').remove()
      g.selectAll('text').remove()
      let rect = g.append('rect')
        .attr('width', barWidth - 1)
        .attr('style', d => 'fill:' + this.getRectColor(d))
        .attr('height', d => y(d.val))
        .attr('y', height)
      rect
        .transition().duration(this.animate ? 300 : 0)
        .attr('y', d => (height - heightPad) - y(d.val))

      if (this.showTooltip) {
        rect.on('mouseover', (event, d) => this.onRectOver(event, d))
          .on('mouseout', (event, d) => this.onRectOut(event, d))
      }

      // Texte
      if (this.showText) {
        var xAxis = d3.axisBottom().scale(x)
        // xAxis.tickSize(0)
        chart.append('g')
          .attr('transform', 'translate(0,' + (height - heightPad - 1) + ')')
          .call(xAxis)
          .selectAll('text')
          .attr('style', d => 'text-anchor:' + (this.incrustText ? 'start' : 'end') + ';fill:' + this.getTextColor(d))
          .attr('transform', 'rotate(' + (270 + this.textAngle) + ')')
          .attr('dy', this.incrustText ? '-0.1em' : '0.5em')
          .attr('x', this.incrustText ? '5' : '-10')
      }

      if (this.showTooltip) {
        // Div porteur du tooltip
        let ttDiv = d3.select('body').select('#tt')
        if (ttDiv.size() < 1) {
          d3.select('body').append('div')
            .attr('id', 'tt')
            .attr('class', 'tooltip')
            .style('opacity', 0)
            .exit().remove()
        }
      }
      this.animate = false
    },
    onRectOver(event, d) {
      let div = d3.select('body').select('#tt')
      div.transition()
        .duration(200)
        .style('opacity', 0.9)
      div.html('<span>' + this.getLegendText(d) + '</span>')
        .style('left', event.pageX + 'px')
        .style('top', (event.pageY - 30) + 'px')
    },
    onRectOut(event, d) {
      let div = d3.select('body').select('#tt')
      div.transition()
        .duration(500)
        .style('opacity', 0)
    }
  }
})
</script>

<style scoped>
.vert-bar-chart-all {
  margin: 10px;
}

.vert-bar-chart,
.vert-bar-chart-legend {
  display: block;
  float: left;
}

.vert-bar-chart>>>rect {
  opacity: 1;
  -webkit-transition: opacity .1s linear;
  -o-transition: opacity .1s linear;
  transition: opacity .1s linear;
}

.vert-bar-chart>>>rect:hover {
  opacity: 0.8;
  -webkit-transition: opacity .2s linear;
  -o-transition: opacity .2s linear;
  transition: opacity .2s linear;
}

>>>h1 {
  color: #0073ab;
}

.vert-bar-chart>>>text {
  fill: #282828;
  font-family: 'Open Sans', 'opensans', Arial, Helvetica, sans-serif;
  font-size: 12px;
}

li {
  list-style: none;
}
</style>

<style>
#tt.tooltip {
  position: absolute;
  z-index: 1000;
}
</style>
